import React, { useState, useEffect, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { CookieConsent } from "./components/CookieConsent";
import { Language } from "./types";

// Páginas carregadas sob demanda (code-splitting) — mantém o bundle inicial
// leve e só carrega o mapa (maplibre-gl) quando se abre a lista de unidades.
const Home = React.lazy(() => import("./pages/Home"));
const UnitsList = React.lazy(() => import("./pages/UnitsList"));
const UnitDetail = React.lazy(() => import("./pages/UnitDetail"));
const Booking = React.lazy(() => import("./pages/Booking"));
const Contact = React.lazy(() => import("./pages/Contact"));
const Legal = React.lazy(() => import("./pages/Legal"));
const Management = React.lazy(() => import("./pages/Management"));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const LoadingFallback = () => (
  <div className="h-screen w-full flex items-center justify-center bg-white">
    <div className="w-12 h-12 border-4 border-slate-200 border-t-amber-600 rounded-full animate-spin"></div>
  </div>
);

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: any) {
    console.error("ErrorBoundary caught:", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen p-8 flex items-center justify-center bg-red-50 text-red-800">
          <div>
            <h2 className="text-xl font-semibold mb-2">An error occurred</h2>
            <pre className="whitespace-pre-wrap">
              {this.state.error?.message}
            </pre>
          </div>
        </div>
      );
    }
    return this.props.children as any;
  }
}

const MainLayout: React.FC<{
  children: React.ReactNode;
  lang: Language;
  setLang: (l: Language) => void;
}> = ({ children, lang, setLang }) => {
  return (
    <div className="flex flex-col min-h-screen">
      <Header lang={lang} setLang={setLang} />
      <main className="flex-grow pt-24 md:pt-20">{children}</main>
      <Footer lang={lang} />
    </div>
  );
};

const App: React.FC = () => {
  const [lang, setLang] = useState<Language>("pt");

  // Mantém o atributo lang do <html> em sincronia com o idioma escolhido (a11y / SEO)
  useEffect(() => {
    document.documentElement.lang = lang === "pt" ? "pt-PT" : "en";
  }, [lang]);

  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<LoadingFallback />}>
        <ErrorBoundary>
          <MainLayout lang={lang} setLang={setLang}>
            <Routes>
              <Route path="/" element={<Home lang={lang} />} />
              <Route path="/unidades" element={<UnitsList lang={lang} />} />
              <Route
                path="/unidades/:slug"
                element={<UnitDetail lang={lang} />}
              />
              <Route path="/reservas" element={<Booking lang={lang} />} />
              <Route path="/gestao" element={<Management lang={lang} />} />
              <Route path="/contactos" element={<Contact lang={lang} />} />
              <Route path="/legal" element={<Legal lang={lang} />} />
              <Route path="*" element={<Home lang={lang} />} />
            </Routes>
          </MainLayout>
        </ErrorBoundary>
      </Suspense>

      <CookieConsent lang={lang} />

      <a
        href="https://wa.me/351930461443"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-40 bg-green-500 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform flex items-center justify-center"
        title="WhatsApp"
        aria-label="Contactar via WhatsApp"
      >
        <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
      </a>
    </Router>
  );
};

export default App;
